import { useEffect } from "react";

import { analytics } from "../../analytics";
import GreenCheckLogo from "../../assets/green-check-logo.svg";
import { useLocationWrapper } from "../../hooks/useLocationWrapper";
import { onBeforeUnloadHandler, sendHostMessage } from "../../utils";
import Button from "../ButtonComponent";

// we are missing the device name, display_name prop or something here to be used in the title
interface IState {
  name: string;
  redirectUrl: string;
  scopedKey: string;
  customerId: string;
  additionalLinks: Array<{
    text: string;
    url: string;
  }>;
  showConnectAnotherDeviceButton?: boolean;
  existingDevicesFound: boolean;
  duplicateConnection?: boolean;
  successMessage: string;
}

interface IPostMessageData {
  scopedKey: string;
  customerId: string;
  duplicateConnection: boolean;
}

// Extended window interface for WebKit message handlers
declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        textureConnect?: {
          postMessage: (data: unknown) => void;
        };
      };
    };
  }
}

const DUPLICATE_CONNECTION_OUTCOME = "duplicate_connection";

function getTargetOrigin(window: Window): string {
  try {
    return window.location.origin;
  } catch {
    return "*";
  }
}

function sendPostMessageToHost({ scopedKey, customerId, duplicateConnection }: IPostMessageData) {
  const messageData = {
    type: "connect-success",
    data: {
      texture_scoped_key: scopedKey,
      texture_customer_id: customerId,
      ...(duplicateConnection ? { texture_connection_outcome: DUPLICATE_CONNECTION_OUTCOME } : {}),
    },
  };

  // Check for iOS WebView bridge first (highest priority)
  if (window.webkit?.messageHandlers?.textureConnect) {
    try {
      window.webkit.messageHandlers.textureConnect.postMessage(messageData);
      return;
    } catch (error) {
      console.warn("Failed to send message via iOS WebView bridge:", error);
    }
  }

  // Fallback to traditional postMessage for web browsers and other environments
  let hostWindow: Window = window;

  if (window.opener && !window.opener.closed) {
    hostWindow = window.opener;
  } else if (window.parent && window.parent !== window) {
    hostWindow = window.parent;
  }

  try {
    const targetOrigin = getTargetOrigin(hostWindow);
    hostWindow.postMessage(messageData, targetOrigin);
  } catch (error) {
    console.warn("Failed to send postMessage to host window:", error);
  }
}

export function ConnectSuccess() {
  const { state } = useLocationWrapper<IState>();
  useEffect(() => {
    if (analytics && analytics.page) {
      analytics.page("connect success");
    }
  }, []);

  const redirectUrl = state?.redirectUrl || "";
  const scopedKey = state?.scopedKey || "";
  const customerId = state?.customerId || "";
  const inPopup = !!window.opener && window.opener !== window;
  const shouldRenderContinue = !!(inPopup || redirectUrl);
  const existingDevicesFound = state?.existingDevicesFound || false;
  const duplicateConnection = state?.duplicateConnection || false;

  useEffect(() => {
    if (!scopedKey || !customerId) {
      console.warn("Scoped key or customer id is not set, skipping post message to host");
      return;
    }

    sendPostMessageToHost({
      scopedKey,
      customerId,
      duplicateConnection,
    });
  }, [scopedKey, customerId, duplicateConnection]);

  const handleContinue = () => {
    if (inPopup) {
      window.removeEventListener("beforeunload", onBeforeUnloadHandler);
      sendHostMessage({
        type: "scoped-key",
        data: scopedKey,
      });
      window.close();
    } else {
      const url = new URL(redirectUrl);
      url.searchParams.append("texture_scoped_key", scopedKey);
      url.searchParams.append("texture_customer_id", customerId);
      if (duplicateConnection) {
        url.searchParams.append("texture_connection_outcome", DUPLICATE_CONNECTION_OUTCOME);
      }
      window.location.assign(url);
    }
  };

  const handleLinkClick = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <>
      <div className="hidden tp:block d:block"></div>
      <div className="flex flex-col items-center justify-between gap-8 tp:gap-4 max-w-[40rem]">
        {!duplicateConnection && <img src={GreenCheckLogo} className="mx-auto w-24 h-24" alt="success-logo" />}
        <div className="text-texturePennBlue font-bold text-xl tp:text-2xl text-center">
          {duplicateConnection
            ? `This account is already connected to ${state.name} for a different customer. Sign in with a different account to continue.`
            : existingDevicesFound
              ? `This account is already connected to ${state.name}. No need to reconnect.`
              : state.successMessage}
        </div>
      </div>
      <div className="w-full sm:w-[30rem] flex flex-col items-center gap-8 tp:gap-4">
        {(state.additionalLinks || []).map((link) => (
          <Button key={link.url} onClick={() => handleLinkClick(link.url)} link secondary>
            {link.text}
          </Button>
        ))}
        {shouldRenderContinue && <Button onClick={handleContinue}>Continue</Button>}
      </div>
    </>
  );
}
